import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, FilePlus, FolderPlus, MoreHorizontal, RefreshCw, Upload, X } from "lucide-react";
import { useRelay } from "../lib/relayContext.tsx";
import type { OpenFiles } from "../lib/useOpenFiles.ts";
import { checkMove } from "../lib/paths.ts";
import {
  baseDir,
  CollisionDialog,
  ExplorerContextMenu,
  ExplorerDialog,
  type ExplorerAction,
} from "./ExplorerDialogs.tsx";
import { FileTree, dropProps, joinPath, parentOf, type TreeSelection } from "./FileTree.tsx";
import { IconButton } from "./IconButton.tsx";
import { Menu } from "./Menu.tsx";
import { useFileTransfer } from "../lib/useFileTransfer.ts";
import { formatBytes } from "../lib/format.ts";

interface Props {
  machineId: string;
  projectId: string;
  projectName: string;
  files: OpenFiles;
  /** Directories changed from outside the explorer (an applied ChangeSet): reload their listings. */
  externalChange?: { version: number; dirs: string[] } | undefined;
}

/** Every ancestor folder of a Project-relative path ("a/b/c.ts" → ["a", "a/b"]). */
const ancestors = (p: string) =>
  p
    .split("/")
    .slice(0, -1)
    .map((_, i, a) => a.slice(0, i + 1).join("/"));

/**
 * File Explorer: tree + toolbar + context menu + internal drag and drop. All operations go through the relay to
 * the agent, which enforces the Project boundary; the UI only chooses Project-relative paths.
 */
export function Explorer({ machineId, projectId, projectName, files, externalChange }: Props) {
  const relay = useRelay();
  const [selected, setSelected] = useState<TreeSelection | null>(null);
  const [dialog, setDialog] = useState<{ action: ExplorerAction; target: TreeSelection | null } | null>(null);
  const [menu, setMenu] = useState<{ sel: TreeSelection; x: number; y: number } | null>(null);
  const [refresh, setRefresh] = useState({ version: 0, dirs: new Set<string>() });
  const [expand, setExpand] = useState(new Set<string>());
  const [dragSource, setDragSource] = useState<TreeSelection | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [rootOpen, setRootOpen] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploadTargetDir, setUploadTargetDir] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reload = useCallback((...dirs: string[]) => {
    setRefresh((r) => ({ version: r.version + 1, dirs: new Set(dirs) }));
  }, []);

  const {
    items: transferItems,
    collisionItem,
    isUploading,
    percent: uploadPercent,
    uploadFiles,
    cancelTransfer,
    cancelAll,
    resolveCollision,
    dismiss: dismissTransfers,
  } = useFileTransfer({
    relay,
    machineId,
    projectId,
    onComplete: (dirs) => reload(...dirs),
    onError: (msg) => setNotice(msg),
  });

  const triggerUpload = (targetDir: string = baseDir(selected)) => {
    setUploadTargetDir(targetDir);
    fileInputRef.current?.click();
  };

  useEffect(() => {
    if (externalChange && externalChange.version > 0) reload(...externalChange.dirs);
  }, [externalChange, reload]);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menu]);

  const start = (action: ExplorerAction, target: TreeSelection | null = selected) => {
    setMenu(null);
    if (action === "upload") {
      triggerUpload(baseDir(target));
      return;
    }
    setDialog({ action, target });
  };

  const create = async (kind: "file" | "folder", dir: string, name: string) => {
    const path = joinPath(dir, name);
    if (kind === "file")
      await relay.machine(machineId, "filesystem/createFile", { projectId, path, content: "" });
    else await relay.machine(machineId, "filesystem/createDirectory", { projectId, path });
    const parents = ancestors(path);
    setExpand(new Set(parents));
    reload(dir, ...parents);
    setSelected({ path, kind: kind === "file" ? "file" : "directory" });
    if (kind === "file") files.open(path);
  };

  /** Rename and move are the same secure operation; the agent refuses to overwrite or leave the Project. */
  const rename = async (target: TreeSelection, newPath: string) => {
    if (newPath === target.path) return;
    if (files.hasDirty(target.path)) {
      throw new Error("Save or close the open files in this location before renaming or moving it.");
    }
    await relay.machine(machineId, "filesystem/rename", { projectId, from: target.path, to: newPath });
    files.renamed(target.path, newPath);
    setExpand(new Set(ancestors(newPath)));
    reload(parentOf(target.path), parentOf(newPath), ...ancestors(newPath));
    setSelected({ ...target, path: newPath });
  };

  const remove = async (target: TreeSelection) => {
    await relay.machine(machineId, "filesystem/delete", {
      projectId,
      path: target.path,
      recursive: target.kind === "directory",
    });
    files.forget(target.path);
    reload(parentOf(target.path));
    setSelected(null);
  };

  const endDrag = () => {
    setDragSource(null);
    setDropTarget(null);
  };

  const drop = (destDir: string) => {
    const source = dragSource;
    endDrag();
    if (!source) return;
    const check = checkMove(source, destDir);
    if (!check.ok) {
      if (!check.silent) setNotice(check.reason);
      return;
    }
    setNotice(null);
    rename(source, check.to).catch((e: unknown) => setNotice((e as Error).message));
  };

  const dnd = {
    dragSource,
    dropTarget,
    onDragStart: setDragSource,
    onDragEnd: endDrag,
    onDropTarget: setDropTarget,
    onDrop: drop,
    onUploadFiles: (destDir: string, files: File[]) => uploadFiles(destDir, files),
  };

  return (
    <div className="explorer">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) {
            uploadFiles(uploadTargetDir, Array.from(e.target.files));
            e.target.value = "";
          }
        }}
        data-testid="fs-upload-input"
      />
      <div className="pane-head">
        <span className="pane-title">Explorer</span>
        <span className="pane-tools">
          <IconButton
            icon={FilePlus}
            label="New File"
            onClick={() => start("newFile")}
            testId="fs-new-file"
          />
          <IconButton
            icon={FolderPlus}
            label="New Folder"
            onClick={() => start("newFolder")}
            testId="fs-new-folder"
          />
          <IconButton
            icon={Upload}
            label="Upload Files…"
            onClick={() => triggerUpload()}
            testId="fs-upload"
          />
          <IconButton icon={RefreshCw} label="Refresh" onClick={() => reload("", ...expand)} />
          <Menu
            testId="fs-more"
            trigger={<MoreHorizontal size={15} strokeWidth={1.75} aria-label="More actions" />}
            items={[
              { label: "Upload Files…", onSelect: () => triggerUpload(), testId: "fs-more-upload" },
              { label: "Rename…", onSelect: () => start("rename"), disabled: !selected, testId: "fs-rename" },
              {
                label: "Delete…",
                onSelect: () => start("delete"),
                disabled: !selected,
                danger: true,
                testId: "fs-delete",
              },
            ]}
          />
        </span>
      </div>
      <div
        className={`tree-root${dropTarget === "" ? " drop-target" : ""}${selected?.path === "" ? " selected" : ""}`}
        role="button"
        tabIndex={0}
        aria-expanded={rootOpen}
        onClick={() => {
          setSelected({ path: "", kind: "directory" });
          setRootOpen((o) => !o);
        }}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            setSelected({ path: "", kind: "directory" });
            setRootOpen((o) => !o);
          }
        }}
        title={`${projectName} (drop here to move to the Project root)`}
        data-testid="tree-root"
        onContextMenu={(ev) => {
          ev.preventDefault();
          setMenu({ sel: { path: "", kind: "directory" }, x: ev.clientX, y: ev.clientY });
        }}
        {...dropProps("", dnd)}
      >
        {rootOpen ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
        <span className="tree-name">{projectName}</span>
      </div>
      {notice && (
        <div className="explorer-notice" role="alert" data-testid="explorer-notice">
          <span>{notice}</span>
          <IconButton icon={X} label="Dismiss" size={12} onClick={() => setNotice(null)} />
        </div>
      )}
      <div style={{ display: rootOpen ? "contents" : "none" }}>
        <FileTree
          machineId={machineId}
          projectId={projectId}
          activePath={files.activePath}
          selected={selected}
          refresh={refresh}
          expand={expand}
          onOpen={files.open}
          onSelect={setSelected}
          onContextMenu={(sel, x, y) => setMenu({ sel, x, y })}
          {...dnd}
        />
      </div>
      {transferItems.length > 0 && (
        <div className="upload-drawer" data-testid="upload-drawer">
          <div className="upload-drawer-head">
            <span className="upload-drawer-title">
              Uploads ({transferItems.filter((i) => i.status === "completed").length}/{transferItems.length})
            </span>
            <span className="upload-drawer-actions">
              {isUploading ? (
                <button className="link-button small" onClick={cancelAll} data-testid="upload-cancel-all">
                  Cancel All
                </button>
              ) : (
                <IconButton
                  icon={X}
                  label="Dismiss"
                  size={12}
                  onClick={dismissTransfers}
                  testId="upload-dismiss"
                />
              )}
            </span>
          </div>
          {isUploading && (
            <div className="upload-progress-bar">
              <div className="upload-progress-fill" style={{ width: `${uploadPercent}%` }} />
            </div>
          )}
          <div className="upload-drawer-list">
            {transferItems.map((item) => (
              <div key={item.id} className="upload-item" data-testid="upload-item">
                <div className="upload-item-info">
                  <span className="upload-item-name" title={item.fileName}>
                    {item.fileName}
                  </span>
                  <span className="upload-item-status">
                    {item.status === "uploading" && (
                      <>
                        {formatBytes(item.bytesUploaded)} / {formatBytes(item.size)} (
                        {Math.round((item.bytesUploaded / (item.size || 1)) * 100)}%)
                      </>
                    )}
                    {item.status === "queued" && "Queued"}
                    {item.status === "completed" && "Completed"}
                    {item.status === "cancelled" && "Cancelled"}
                    {item.status === "collision" && "File exists"}
                    {item.status === "error" && (item.error || "Failed")}
                  </span>
                </div>
                <div className="upload-item-actions">
                  {(item.status === "uploading" || item.status === "queued") && (
                    <IconButton
                      icon={X}
                      label="Cancel upload"
                      size={12}
                      onClick={() => cancelTransfer(item.id)}
                      testId={`upload-cancel-${item.id}`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {menu && (
        <ExplorerContextMenu
          x={menu.x}
          y={menu.y}
          kind={menu.sel.kind}
          isRoot={menu.sel.path === ""}
          onAction={(a) => start(a, menu.sel)}
        />
      )}
      {dialog && (
        <ExplorerDialog
          action={dialog.action}
          target={dialog.target}
          projectName={projectName}
          targetHasDirtyTabs={dialog.target ? files.hasDirty(dialog.target.path) : false}
          onClose={() => setDialog(null)}
          onCreate={create}
          onRename={rename}
          onDelete={remove}
        />
      )}
      {collisionItem && (
        <CollisionDialog
          fileName={collisionItem.fileName}
          targetDir={collisionItem.targetDir}
          projectName={projectName}
          onResolve={(action) => resolveCollision(collisionItem, action)}
        />
      )}
    </div>
  );
}
